import React, { createContext, useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import LoginPage from "./pages/User/LoginPage";
import TopNavBar from "./components/Navigation/TopNavbar";
import SiteRoutes from "./components/Navigation/SiteRoutes";
import { ToastContainer } from "react-toastify";
import {
  createBeforeUnloadHandler,
  initializeApp,
  persistCart,
  showToast,
} from "./config_and_helpers/helpers";
import CartModal from "./components/Shop/CartModal";
import AiChatShell from "./components/AiChat/AiChatShell";
import BottomNavBar from "./components/Navigation/BottomNavBar";
import LoadingScreen from "./LoadingScreen";
import { downloadLogFile, logError, logInfo } from "./config_and_helpers/logger";
import { AI_CHAT_ENABLED } from "./config_and_helpers/config";
import { getCurrentLab } from "./clients/lab_client";
import { isLabViewer } from "./utils/userAccountRole";

// General App context responsible for user related states
export const AppContext = createContext(null);

// Cart context responsible for the shopping cart states
export const CartAppContext = createContext(null);

// Order context responsible for updating states if an order is deleted
export const OrderDeletionContext = createContext();
/**
 * This is the root component of the application.
 * The entire app state is maintained here including token, user details, supplier status, notification settings, cart status and cart content.
 * It consists of hooks for initialising the app, handling session storage and managing the shopping cart.
 * The component also boasts context providers for sharing app-wide state like authentication token, user details, cart status and cart content among other child components.
 * Depending on the token status, it conditionally renders the main site routes or the login page.
 *
 * @component
 *
 * @returns {JSX.Element} Returns the JSX for the Entire App's DOM hierarchy.
 */
function App() {
  // State hooks
  const [isLoading, setIsLoading] = useState(true); // Indicates whether the user's details are being loaded at startup
  const [token, setToken] = useState(null); // Token for the current user's session
  const [userDetails, setUserDetails] = useState({}); // Details about the currently authenticated user
  const [isSupplier, setIsSupplier] = useState(false); // Flag indicating whether the current user is a supplier
  const [rememberMe, setRememberMe] = useState(false); // Flag for remembering the user's session
  const [notifications, setNotifications] = useState({}); // Object containing boolean values whether new notifications exist
  const [notificationsSeen, setNotificationsSeen] = useState(false); // Flag indicating if notifications toast was seen
  const [showCart, setShowCart] = useState(false); // Flag for showing the cart modal
  const [cart, setCart] = useState([]); // List of items in the cart
  const [cartCount, setCartCount] = useState(0); // Number of items in the cart
  const [isOrderDeleted, setIsOrderDeleted] = useState(false); // Is an order deleted
  const [currentLab, setCurrentLab] = useState(null); // Current lab tenant (lab users only; not persisted)

  const labViewer = isLabViewer(userDetails, isSupplier);

  const location = useLocation();
  /** Full-width pages: no top bar / cart (public forms + internal worker-timesheet dashboard). */
  const hideNav =
    ["/experiment_form", "/worker_timesheet"].includes(location.pathname) ||
    location.pathname.startsWith("/worker-timesheets") ||
    location.pathname.startsWith("/request") ||
    location.pathname.startsWith("/inventory/open") ||
    location.pathname.startsWith("/organoid-requests/access");

  // Function for toggling the is order deleted state
  const toggleOrderDeleted = () => {
    setIsOrderDeleted((prevState) => !prevState);
  };

  const handleDownloadListSearchLog = useCallback(() => {
    const filename = `list-search-log-${new Date()
      .toISOString()
      .replace(/[:.]/g, "-")}.txt`;
    downloadLogFile(filename);
  }, []);

  // useEffect hook for initialization
  useEffect(() => {
    (async () => {
      // Initialize application state with data from local/session storage
      await initializeApp(
        setRememberMe,
        setToken,
        setUserDetails,
        setNotifications,
        setIsSupplier,
        setIsLoading,
        setCart,
        setNotificationsSeen,
      );
    })();
  }, []);

  // useEffect hook for local/session storage
  useEffect(() => {
    let storageType = rememberMe ? "localStorage" : "sessionStorage";
    // Create a handler for the 'beforeunload' event
    const handleBeforeUnload = createBeforeUnloadHandler(
      storageType,
      token,
      userDetails,
      notifications,
      isSupplier,
      rememberMe,
      cart,
      notificationsSeen,
    );

    // Add the event listener for 'beforeunload'
    window.addEventListener("beforeunload", handleBeforeUnload);

    // Cleanup function to remove the event listener when the component unmounts
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [
    token,
    userDetails,
    notifications,
    isSupplier,
    rememberMe,
    cart,
    notificationsSeen,
  ]);

  // useEffect hook for cart
  useEffect(() => {
    // Update cartCount whenever cart changes
    setCartCount(cart?.length);
    persistCart(cart);
  }, [cart]);

  // UseEffect hook for notifications toast
  useEffect(() => {
    //   If notifications exist, show a toast indicating that and set the notificationSeen to true
    if (
      notifications?.order_notifications === true &&
      !notificationsSeen &&
      !isSupplier
    ) {
      showToast("New notifications waiting... ", "info", "top-center", false);
      setNotificationsSeen(true);
    }
  }, [notifications, notificationsSeen, isSupplier]);

  // Fetch current lab tenant after login / session restore (lab users only; refetch each session).
  useEffect(() => {
    if (!token || isSupplier) {
      setCurrentLab(null);
      return;
    }

    let cancelled = false;

    (async () => {
      const result = await getCurrentLab(token);
      if (cancelled) return;
      if (result.success) {
        setCurrentLab(result.data);
        logInfo('App', 'currentLab loaded', { labId: result.data?.id });
      } else {
        setCurrentLab(null);
        logError('App', 'getCurrentLab failed on session bootstrap', {
          status: result.status ?? null,
          messages: result.messages,
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token, isSupplier]);

  // A Viewer session has no AI chat and no cart; log once per user so a burst of
  // backend auth.viewer_write_blocked lines can be matched to a missing UI gate.
  useEffect(() => {
    if (!token || !labViewer) {
      return;
    }
    logInfo("App", "read-only viewer session", {
      userId: userDetails?.user_id ?? null,
      accountRole: userDetails?.account_role ?? null,
    });
  }, [token, labViewer, userDetails?.user_id, userDetails?.account_role]);

  // Render the core application components
  return (
    <div>
      {isLoading && <LoadingScreen />}
      <ToastContainer />

      <AppContext.Provider
        value={{
          token,
          setToken,
          rememberMe,
          setRememberMe,
          userDetails,
          setUserDetails,
          isSupplier,
          setIsSupplier,
          setNotifications,
          setNotificationsSeen,
          currentLab,
          setCurrentLab,
        }}
      >
        <CartAppContext.Provider
          value={{
            showCart,
            setShowCart,
            cart,
            setCart,
            cartCount,
            setCartCount,
          }}
        >
          <OrderDeletionContext.Provider
            value={{ isOrderDeleted, toggleOrderDeleted }}
          >
            {token ? (
              <>
                {!hideNav && (
                  <TopNavBar onDownloadListSearchLog={handleDownloadListSearchLog} />
                )}
                <SiteRoutes />
                {!hideNav && !labViewer && (
                  <CartModal show={showCart} setShow={setShowCart} />
                )}
                {!hideNav &&
                  AI_CHAT_ENABLED &&
                  !isSupplier &&
                  !labViewer && (
                    <AiChatShell />
                  )}
              </>
            ) : (
              <>
                {/* Public forms (no login): experiment, timesheet, OC request portal */}
                {["/experiment_form", "/worker_timesheet"].includes(
                  location.pathname,
                ) ||
                location.pathname.startsWith("/request") ||
                location.pathname.startsWith("/inventory/open") ||
                location.pathname.startsWith("/organoid-requests/access") ? (
                  <SiteRoutes />
                ) : (
                  <>
                    <LoginPage />
                    <BottomNavBar />
                  </>
                )}
              </>
            )}
          </OrderDeletionContext.Provider>
        </CartAppContext.Provider>
      </AppContext.Provider>
    </div>
  );
}

export default App;
